import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useParticipant } from "@/context/ParticipantContext";
import { REGISTRATION } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";
import { titleCase } from "@/lib/format";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_60dfbe01-e8f1-47bb-9c72-2a0d72443632/artifacts/waarlbny_DENSO-main-logo-web.jpg";

export default function Registration() {
  const { register } = useParticipant();
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !surname.trim() || !mobile.trim()) {
      setError("Please complete all fields before continuing.");
      return;
    }
    if (!/^[0-9+\s]{7,15}$/.test(mobile.trim())) {
      setError("Please enter a valid mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await register({
        name: titleCase(name),
        surname: titleCase(surname),
        mobile: mobile.trim(),
      });
      if (data?.resumed) {
        toast.success(`Welcome back, ${data.name}!`, {
          description: "We've loaded your existing training progress.",
        });
      }
      // Participant state update flips the "/" route (App.js) to redirect
      // to /home or /certificate based on the participant's certified status.
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-4 sm:p-6"
      style={{ backgroundColor: "#E4E4E7" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="bg-denso-red px-6 py-6 text-center">
          <img src={LOGO_URL} alt="DENSO" className="h-12 mx-auto rounded-sm mb-3" />
          <h1 className="text-white font-heading font-bold text-xl">Product Training Platform</h1>
          <p className="text-white/80 text-sm mt-1">Trainee Registration</p>
        </div>

        <p className="text-xs text-slate-500 text-center px-6 pt-4">
          Already registered? Just enter the same mobile number to resume where you left off.
        </p>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Name
              </Label>
              <Input
                data-testid={REGISTRATION.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Surname</Label>
              <Input
                data-testid={REGISTRATION.surnameInput}
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                placeholder="Surname"
				style={{ marginTop: 0 }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" /> Mobile Number
            </Label>
            <Input
              data-testid={REGISTRATION.mobileInput}
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="082 123 4567"
            />
          </div>

          {error && (
            <p data-testid={REGISTRATION.errorMessage} className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid={REGISTRATION.continueButton}
            disabled={submitting}
            className="w-full bg-denso-red hover:bg-denso-lightred text-white h-11 font-semibold transition-all duration-200 hover:-translate-y-0.5"
          >
            {submitting ? "Please wait..." : "Continue"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
